import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { generateHealthSummary } from '../utils/healthSummary';

const OrganHealth = () => {
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState({});
  const [organHealthData, setOrganHealthData] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);

  const organs = [
    { 
      name: 'Brain', 
      icon: '🧠', 
      key: 'brain',
      description: 'Cognitive function and mental wellness',
      color: 'purple'
    },
    { 
      name: 'Heart', 
      icon: '❤️', 
      key: 'heart',
      description: 'Cardiovascular health and function',
      color: 'red'
    },
    { 
      name: 'Liver', 
      icon: '🟢', 
      key: 'liver',
      description: 'Liver function and detoxification',
      color: 'green'
    },
    { 
      name: 'Lungs', 
      icon: '🫁', 
      key: 'lung',
      description: 'Respiratory health and capacity',
      color: 'blue'
    },
    { 
      name: 'Stomach', 
      icon: '🟡', 
      key: 'stomach',
      description: 'Digestive system health',
      color: 'yellow'
    }
  ];

  useEffect(() => {
    loadOrganHealthData();
  }, []);

  const loadOrganHealthData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/organ-health/latest');
      const organHealth = response.data.organHealth || [];
      
      if (organHealth.length === 0) {
        // No organ health data found
        setPredictions({});
        setOrganHealthData([]);
        return;
      }
      
      // Convert organ health array to predictions object
      const predictionsData = {};
      organHealth.forEach(organ => {
        // Map organ names correctly - backend sends capitalized names
        const organKey = organ.organ_name.toLowerCase();
        predictionsData[organKey] = {
          health_score: organ.health_score,
          risk_level: organ.risk_level,
          prediction_data: organ.prediction_data
        };
      });
      
      setPredictions(predictionsData);
      setOrganHealthData(organHealth);
      
      // Generate structured health summary
      const healthPercentages = {};
      organHealth.forEach(organ => {
        const key = organ.organ_name.toLowerCase();
        healthPercentages[key] = organ.health_score;
      });
      const summary = generateHealthSummary(healthPercentages);
      setHealthSummary(summary);
    } catch (error) {
      console.error('Failed to load organ health data:', error);
      toast.error('Failed to load organ health data');
      setPredictions({});
      setOrganHealthData([]);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      'very_low': 'text-green-600 bg-green-100',
      'low': 'text-blue-600 bg-blue-100',
      'moderate': 'text-yellow-600 bg-yellow-100',
      'high': 'text-orange-600 bg-orange-100',
      'very_high': 'text-red-600 bg-red-100'
    };
    return colors[riskLevel] || colors.low;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Organ Health Analysis</h1>
        <p className="text-gray-600">AI-powered health predictions based on your daily patterns and lifestyle data</p>
      </div>

      {/* Action Bar - Only show when no data */}
      {organHealthData.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Health Predictions</h2>
              <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Organs Grid */}
      {organHealthData.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {organs.map((organ) => {
            const prediction = predictions[organ.key];
            const riskColorClass = getRiskColor(prediction?.risk_level);
            const scoreColorClass = getScoreColor(prediction?.health_score);
            
            return (
              <div key={organ.key} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className="text-3xl mr-3">{organ.icon}</span>
                    <h3 className="text-xl font-semibold text-gray-900">{organ.name}</h3>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${riskColorClass}`}>
                    {prediction?.risk_level?.replace('_', ' ') || 'Unknown'}
                  </span>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Health Score</span>
                    <span className={`text-2xl font-bold ${scoreColorClass}`}>
                      {prediction?.health_score || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        prediction?.health_score >= 80 ? 'bg-green-500' :
                        prediction?.health_score >= 60 ? 'bg-blue-500' :
                        prediction?.health_score >= 40 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${prediction?.health_score || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="text-sm text-gray-600 mb-4">
                  {organ.description}
                </div>

                <div className="text-sm text-gray-600">
                  {prediction?.risk_level?.replace('_', ' ').toUpperCase() || 'UNKNOWN'} RISK
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* No Data State */
        <div className="text-center py-12">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <div className="text-6xl mb-4">🏥</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Health Data Available</h2>
            <p className="text-gray-600 mb-6">
              You haven't completed a comprehensive health assessment yet. Take the questionnaire to get your personalized organ health predictions.
            </p>
            <button
              onClick={() => window.location.href = '/questionnaire'}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Take Health Assessment
            </button>
          </div>
        </div>
      )}

      {/* Structured Overall Health Summary Card */}
      {healthSummary && organHealthData.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">📊</span>
            <h2 className="text-xl font-bold text-gray-900">Overall Health Summary</h2>
          </div>
          
          {/* Summary Text */}
          <p className="text-gray-700 leading-relaxed mb-4">
            {healthSummary.summary}
          </p>
          
          {/* Highlights & Concerns Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Highlights */}
            {healthSummary.highlights.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <span>✅</span> Highlights
                </h3>
                <ul className="space-y-1">
                  {healthSummary.highlights.map((highlight, idx) => (
                    <li key={idx} className="text-sm text-green-700">{highlight}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Concerns */}
            {healthSummary.concerns.length > 0 && (
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                  <span>⚠️</span> Areas to Improve
                </h3>
                <ul className="space-y-1">
                  {healthSummary.concerns.map((concern, idx) => (
                    <li key={idx} className="text-sm text-orange-700">{concern}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* No concerns state */}
            {healthSummary.concerns.length === 0 && healthSummary.highlights.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <span>🎉</span> Great News
                </h3>
                <p className="text-sm text-blue-700">All organs are performing well! Keep up your healthy habits.</p>
              </div>
            )}
          </div>
          
          {/* Actionable Suggestion */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-l-4 border-blue-500">
            <h3 className="font-semibold text-blue-800 mb-1 flex items-center gap-2">
              <span>💡</span> Suggestion
            </h3>
            <p className="text-sm text-blue-700">{healthSummary.suggestion}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganHealth;
